/* MODE LAPANGAN — CUSTOMER SCREENS + MANUAL INPUTS: Pelanggan, customer sheet, Lengkapi data, Atur titik,
   Tambah stop, Pembayaran bon, Ganti rugi galon, Pengeluaran (Hitung galon lives in dist-galon.jsx). Every
   read/write goes through the adaptor `api` the shell hands in (real server or this phone's practice copy) —
   never the server directly. Owner rules kept here: a proof photo for every money-in and damage charge and
   every expense; expenses always cash from the deposit; a pin moved more than 150 m from the phone asks to
   confirm. */

// Avatar tints of the board, steady per customer (not per row, so a filter never recolours a person).
const FLD_AVA_TINTS = [['#E8F1F8', '#065489'], ['#DDF4F2', '#0F6B66'], ['#EEE9F8', '#4B3A8C'], ['#FCF1D6', '#7A4B00']];
const fldTint = (id) => FLD_AVA_TINTS[String(id || '').split('').reduce((t, ch) => t + ch.charCodeAt(0), 0) % FLD_AVA_TINTS.length];
const fldInitials = (name) => String(name || '?').split(/\s+/).map((w) => w.charAt(0)).slice(0, 2).join('').toUpperCase();

// PELANGGAN (mockup Pelanggan board): filter chips that scroll sideways, round tinted avatars, the
// missing-data tags, the bon (or a green "Lunas") with the gallons held, and the glass search pill
// floating above the dock.
function FldCustomers({ api, tick, filter, onFilter, onOpen, selectedId }) {
  const [list, setList] = uSfl(null);
  const [err, setErr] = uSfl(null);
  const [q, setQ] = uSfl('');
  uEfl(() => { let live = true; setErr(null); api.customers().then((r) => { if (live) setList(r || []); }).catch((e) => { if (live) setErr(e); }); return () => { live = false; }; }, [api, tick]);
  if (err) return <FldNotice tone="warn" alert title={trFl('fld.loadErr')} sub={fldErrMsg(err)} />;
  if (!list) return <div className="mlap-empty">{trFl('fld.loading')}</div>;
  const v = FIELDLOGIC.customerList(list, { q, filter });
  const chips = [['all', trFl('fld.f_all', { n: v.counts.all })], ['warn', trFl('fld.f_warn', { n: v.counts.warn })], ['bon', trFl('fld.f_bon', { n: v.counts.bon })], ['fixed', trFl('fld.f_fixed', { n: v.counts.fixed })]];
  return (
    <>
      <div className="mlap-hscroll" role="group" aria-label={trFl('fld.filter')}>
        {chips.map(([k, text]) => <button key={k} type="button" className={'mlap-fchip' + (k === 'warn' ? ' warn' : '') + (filter === k ? ' on' : '')} aria-pressed={filter === k} onClick={() => onFilter(k)}>{text}</button>)}
      </div>
      <div className="mlap-card mlap-list">
        {v.rows.length ? v.rows.map((c) => {
          const t = fldTint(c.id);
          const warns = [c.gaps.titik ? 'fld.tagNoPin' : '', c.gaps.wa ? 'fld.noWa' : '', c.gaps.foto ? 'fld.tagNoFoto' : ''].filter(Boolean);
          return (
            <button key={c.id} type="button" className={'mlap-row mlap-rowbtn mlap-custrow' + (selectedId === c.id ? ' on' : '')} aria-current={selectedId === c.id ? 'true' : undefined} onClick={() => onOpen(c)}>
              <span className="mlap-ava" aria-hidden="true" style={{ background: t[0], color: t[1] }}>{fldInitials(c.name)}</span>
              <span className="mlap-grow">
                <span className="nm">{c.name}</span>
                <span className="sb">{[c.code, (c.deliveryDays || []).join(' · '), c.fixedDays ? trFl('fld.fixedLow') : ''].filter(Boolean).join(' · ')}</span>
                {warns.length || c.countRequest ? <span className="mlap-gtags">{c.countRequest ? <span className="mlap-gtag mlap-hg-gtag">{trFl('hg.count')}</span> : null}{warns.map((k) => <span key={k} className="mlap-gtag">{trFl(k)}</span>)}</span> : null}
              </span>
              <span className="mlap-custside">
                {c.sisaBon > 0 ? <b className="bon">{FIELDLOGIC.fmtRp(c.sisaBon)}</b> : <b className="ok">{trFl('fld.lunas')}</b>}
                <span className="sb">{trFl('fld.nGalon', { n: c.gallonsHeld || 0 })}</span>
              </span>
            </button>
          );
        }) : <div className="mlap-empty">{trFl('fld.noCustFilter')}</div>}
      </div>
      <div className="mlap-ctaspace" />
      <label className="mlap-glass mlap-searchpill">
        <FldSvg n="search" s={16} sw={2.2} />
        <input type="search" placeholder={trFl('fld.searchCust2')} aria-label={trFl('fld.searchCust')} value={q} onChange={(e) => setQ(e.target.value)} />
      </label>
    </>
  );
}

// The customer's sheet (same look as Detail stop): avatar + name + close X, Navigasi / Telepon / WhatsApp,
// bon and gallons held, the missing data, then the actions this account may use.
function FldCustSheet({ cust: c0, can, onClose, onAction, onDetail }) {
  const drag = useFldSheetDrag(onClose);
  const c = Object.assign({}, c0, { gaps: c0.gaps || FIELDLOGIC.gapsOf(c0) });
  const links = fldLinks(c);
  const t = fldTint(c.id);
  const acts = [];
  if (can.sale) acts.push(['sale', 'receipt', 'fld.catatSale']);
  if (can.bon && c.sisaBon > 0) acts.push(['bon', 'cash', 'fld.terimaBon']);
  if (can.location && c.gaps.count > 0) acts.push(['complete', 'pinMove', 'fld.completeData']);
  if (can.addStop) acts.push(['addStop', 'pinPlus', 'fld.addToday']);
  if (can.adjust) acts.push(['adjust', 'adjust', 'hg.count']);
  acts.push(['gallon', 'clipboard', 'hg.historyRow']);   // every count and delivery, and the Kartu Galon
  if (can.damage && c.gallonsHeld > 0) acts.push(['damage', 'bottleBroken', 'fld.dmgRow']);
  if (c.countRequest) acts.sort((a, b) => (b[0] === 'adjust') - (a[0] === 'adjust'));   // asked for: Hitung galon comes first (stable; no-op without the cap)
  const fix = can.location && c.gaps.count > 0 ? () => onAction('complete', c) : null;
  const warns = [c.gaps.titik ? 'fld.tagNoPin' : '', c.gaps.wa ? 'fld.noWa' : '', c.gaps.foto ? 'fld.tagNoFoto' : ''].filter(Boolean);
  return (
    <>
      <button type="button" className="mlap-scrim" aria-label={trFl('fld.cancel')} onClick={onClose} />
      <div className="mlap-sheet" role="dialog" aria-modal="true" aria-label={c.name} ref={drag.ref} style={drag.style}>
        <FldGrab handle={drag.handle} />
        <FldSheetHead title={c.name} sub={[c.code, c.address].filter(Boolean).join(' · ')} lead={<span className="mlap-ava lg" aria-hidden="true" style={{ background: t[0], color: t[1] }}>{fldInitials(c.name)}</span>} onClose={onClose} />
        <div className="mlap-sheet-stack">
          <div className="mlap-contacts">
            <FldLinkBtn href={links.nav} className="mlap-ctile" newTab><FldSvg n="navigate" s={18} />{trFl('fld.navigate')}</FldLinkBtn>
            <FldLinkBtn href={links.tel} className="mlap-ctile"><FldSvg n="phone" s={18} />{trFl('fld.call')}</FldLinkBtn>
            {links.wa ? <FldLinkBtn href={links.wa} className="mlap-ctile" newTab><FldSvg n="wa" s={18} />{trFl('fld.wa')}</FldLinkBtn>
              : fix ? <button type="button" className="mlap-ctile miss" onClick={fix}><FldSvg n="wa" s={18} />{trFl('fld.fillWaTile')}</button>
                : <FldLinkBtn href="" className="mlap-ctile"><FldSvg n="wa" s={18} />{trFl('fld.wa')}</FldLinkBtn>}
          </div>
          <div className="mlap-card mlap-facts2">
            <div><span className="sb">{trFl('fld.bonNow')}</span>{c.sisaBon > 0 ? <b className="bon">{FIELDLOGIC.fmtRp(c.sisaBon)}</b> : <b className="ok">{trFl('fld.lunas')}</b>}</div>
            <div><span className="sb">{trFl('fld.heldAt')}</span><b>{c.gallonsHeld == null ? '—' : c.gallonsHeld}</b></div>
          </div>
          {warns.length ? <span className="mlap-gtags">{warns.map((k) => <span key={k} className="mlap-gtag">{trFl(k)}</span>)}</span> : null}
          {onDetail ? <button type="button" className="mlap-btn mlap-wide mlap-histbtn" onClick={() => onDetail(c)}><FldSvg n="clock" s={17} />{trFl('fld.seeHistory')}</button> : null}
          {acts.length ? (
            <div className="mlap-card">
              {acts.map(([k, ico, key]) => (
                <button key={k} type="button" className="mlap-actrow" onClick={() => onAction(k, c)}>
                  <FldSvg n={ico} s={17} /><span className="mlap-grow">{trFl(key)}</span><FldSvg n="chevron" s={13} sw={2.4} />
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
}

// DETAIL PELANGGAN (owner, 2026-10: the field team must see a customer's history). One page with the
// customer's numbers on top (bon, gallons held, price, last delivery), then two segments: Riwayat — every
// transaction and adjustment, newest first, by month, filterable, a row opens its details (proof photo,
// who recorded it, "Ajukan koreksi") — and Info (address, days, armada, location, price changes). The
// actions this account may use stay at hand. On a phone it is a full page; on a tablet it sits beside
// the Pelanggan list (embedded: no back button).
const FLD_HIST_T = { jual: 'fld.h_jual', bon: 'fld.h_bon', bayar: 'fld.h_bayar', gantiRugi: 'fld.h_gantiRugi' };
const FLD_HIST_ICO = { jual: 'truck', bon: 'receipt', bayar: 'cash', gantiRugi: 'bottleBroken', adj: 'adjust' };
const FLD_HIST_PAGE = 40;
const FLD_VIA_T = { tunai: 'fld.m_tunai', transfer: 'fld.m_transfer' };
// how a row was paid, in words: a paid sale by its way (cash / transfer), a bon as Bon, a bon payment by its way
const fldPayText = (r) => (r.kind === 'bon' ? trFl('fld.m_bon') : r.kind === 'bayar' ? trFl('fld.m_pelunasan') + ' · ' + trFl(FLD_VIA_T[r.via]) : trFl(FLD_VIA_T[r.via]));
const fldShortDay = (iso) => { try { return new Date(iso + 'T00:00').toLocaleDateString(trFl('fld.locale'), { weekday: 'short', day: 'numeric', month: 'short' }); } catch (e) { return iso; } };
const fldMonthLabel = (ym) => { try { return new Date(ym + '-01T00:00').toLocaleDateString(trFl('fld.locale'), { month: 'long', year: 'numeric' }); } catch (e) { return ym; } };
const fldWhen = (ms) => { try { return new Date(ms).toLocaleDateString(trFl('fld.locale'), { day: 'numeric', month: 'short', year: 'numeric' }); } catch (e) { return ''; } };

function FldHistRow({ api, r, open, canKoreksi, onToggle, onKoreksi }) {
  const [photo, setPhoto] = uSfl(null);   // null | 'busy' | 'err' | data URL
  const isAdj = r.src === 'adj';
  // a delivery leads with its gallons (what the driver looks for), then paid / bon, the way and the empties back
  const sale = r.kind === 'jual' || r.kind === 'bon';
  const title = isAdj ? trFl(r.adjKind === 'bon' ? 'fld.h_adjBon' : 'fld.h_adjGalon') : sale ? trFl('fld.nGalon', { n: r.qty }) : trFl(FLD_HIST_T[r.kind]);
  const sub = isAdj
    ? [(r.delta > 0 ? '+' : '') + (r.adjKind === 'bon' ? FIELDLOGIC.fmtRp(r.delta) : trFl('fld.nGalon', { n: r.delta })), r.reason, r.note].filter(Boolean).join(' · ')
    : r.kind === 'gantiRugi' ? trFl('fld.nGalon', { n: r.gallonQty })
      : r.kind === 'bayar' ? trFl(FLD_VIA_T[r.via])
        : [trFl(FLD_HIST_T[r.kind]), r.kind === 'jual' ? trFl(FLD_VIA_T[r.via]) : '', r.gallonIn ? trFl('fld.h_back', { n: r.gallonIn }) : ''].filter(Boolean).join(' · ');
  const tags = [r.voided ? ['fld.h_void', 'neg'] : null, r.pending ? ['fld.h_wait', 'warn'] : null, r.legacy ? ['fld.h_arsip', ''] : null, r.corrected ? ['fld.h_fixed', ''] : null].filter(Boolean);
  const showPhoto = () => {
    setPhoto('busy');
    api.photo(r.proofPhotoId).then((d) => setPhoto(d || 'err')).catch(() => setPhoto('err'));
  };
  const day = r.txnDate ? new Date(r.txnDate + 'T00:00') : null;
  return (
    <div className={'mlap-hrow' + (r.voided ? ' void' : '') + (open ? ' open' : '')}>
      <button type="button" className="mlap-hrow-b" aria-expanded={isAdj ? undefined : open} onClick={isAdj ? undefined : onToggle} disabled={isAdj}>
        <span className="mlap-hday" aria-hidden="true"><b>{day ? day.getDate() : '—'}</b><span>{day ? day.toLocaleDateString(trFl('fld.locale'), { weekday: 'short' }) : ''}</span></span>
        <span className={'mlap-hico ' + r.kind} aria-hidden="true"><FldSvg n={FLD_HIST_ICO[r.kind] || 'receipt'} s={15} /></span>
        <span className="mlap-grow">
          <span className="nm">{title}</span>
          <span className="sb">{sub}</span>
          {tags.length ? <span className="mlap-htags">{tags.map(([k, tone]) => <span key={k} className={'mlap-htag ' + tone}>{trFl(k)}</span>)}</span> : null}
        </span>
        {!isAdj ? <b className={'mlap-hamt' + (r.kind === 'bayar' ? ' in' : r.kind === 'bon' ? ' bon' : '')}>{r.kind === 'bayar' ? '+' : ''}{FIELDLOGIC.fmtRp(r.amount)}</b> : null}
        {!isAdj ? <FldSvg n="chevDown" s={13} sw={2.4} /> : null}
      </button>
      {open && !isAdj ? (
        <div className="mlap-hmore">
          <dl className="mlap-dl">
            <div><dt>{trFl('fld.h_date')}</dt><dd>{fldShortDay(r.txnDate)}{r.at ? ' · ' + new Date(r.at).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' }) : ''}</dd></div>
            {r.kind !== 'bayar' && r.kind !== 'gantiRugi' ? <div><dt>{trFl('fld.h_price')}</dt><dd>{FIELDLOGIC.fmtRp(r.unitPrice)}</dd></div> : null}
            {r.kind !== 'bayar' && r.kind !== 'gantiRugi' ? <div><dt>{trFl('fld.h_gallons')}</dt><dd>{trFl('fld.h_outIn', { o: r.gallonOut || r.qty, i: r.gallonIn })}</dd></div> : null}
            <div><dt>{trFl('fld.h_pay')}</dt><dd>{fldPayText(r)}</dd></div>
            {r.actorName ? <div><dt>{trFl('fld.h_by')}</dt><dd>{r.actorName}</dd></div> : null}
            {r.note ? <div><dt>{trFl('fld.h_note')}</dt><dd>{r.note}</dd></div> : null}
          </dl>
          {r.proofPhotoId ? (
            typeof photo === 'string' && photo.indexOf('data:') === 0
              ? <img className="mlap-hphoto" src={photo} alt={trFl('fld.proof')} />
              : <button type="button" className="mlap-btn ghost" disabled={photo === 'busy'} onClick={showPhoto}><FldSvg n="camera" s={16} />{photo === 'busy' ? trFl('fld.loading') : photo === 'err' ? trFl('fld.h_photoErr') : trFl('fld.h_photo')}</button>
          ) : null}
          {canKoreksi && !r.voided && !r.pending && !r.legacy && r.kind !== 'gantiRugi'
            ? <button type="button" className="mlap-btn ghost" onClick={onKoreksi}><FldSvg n="pen" s={16} />{trFl('fld.koreksiT')}</button> : null}
        </div>
      ) : null}
    </div>
  );
}

function FldCustDetail({ api, cust: c0, can, today, tick, embedded, onBack, onAction }) {
  const [d, setD] = uSfl(null);
  const [err, setErr] = uSfl(null);
  const [reload, setReload] = uSfl(0);
  const [seg, setSeg] = uSfl('riwayat');
  const [filter, setFilter] = uSfl('all');
  const [openId, setOpenId] = uSfl('');
  const [shown, setShown] = uSfl(FLD_HIST_PAGE);
  uEfl(() => {
    let live = true; setErr(null);
    api.customerDetail(c0.id).then((x) => { if (live) setD(x || {}); }).catch((e) => { if (live) setErr(e); });
    return () => { live = false; };
  }, [api, c0.id, tick, reload]);
  // a new customer (tablet: another row picked) starts on its own history
  uEfl(() => { setD(null); setSeg('riwayat'); setFilter('all'); setOpenId(''); setShown(FLD_HIST_PAGE); }, [c0.id]);
  // the list's row first (instant), the server's detail over it once it is here (a hidden balance stays hidden)
  const c = Object.assign({}, c0, d ? Object.keys(d).reduce((o, k) => { if (d[k] !== undefined && k !== 'transactions') o[k] = d[k]; return o; }, {}) : {});
  c.gaps = FIELDLOGIC.gapsOf(c);
  const h = d ? FIELDLOGIC.custHistory(d, { filter, today }) : null;
  const links = fldLinks(c);
  const t = fldTint(c.id);
  const acts = [];
  if (can.sale) acts.push(['sale', 'receipt', 'fld.catatSale']);
  if (can.bon && c.sisaBon > 0) acts.push(['bon', 'cash', 'fld.terimaBon']);
  if (can.location && c.gaps.count > 0) acts.push(['complete', 'pinMove', 'fld.completeData']);
  if (can.addStop) acts.push(['addStop', 'pinPlus', 'fld.addToday']);
  if (can.adjust) acts.push(['adjust', 'adjust', 'hg.count']);
  acts.push(['gallon', 'clipboard', 'hg.historyRow']);   // every count and delivery, and the Kartu Galon
  if (can.damage && c.gallonsHeld > 0) acts.push(['damage', 'bottleBroken', 'fld.dmgRow']);
  const fix = can.location && c.gaps.count > 0 ? () => onAction('complete', c) : null;
  const warns = [c.gaps.titik ? 'fld.tagNoPin' : '', c.gaps.wa ? 'fld.noWa' : '', c.gaps.foto ? 'fld.tagNoFoto' : ''].filter(Boolean);
  const st = h ? h.stats : null;
  const last = st && st.lastDate ? (st.daysSince === 0 ? trFl('fld.h_today') : trFl('fld.h_daysAgo', { n: st.daysSince })) : '—';
  const chips = h ? [['all', 'fld.hf_all', h.counts.all], ['kirim', 'fld.hf_kirim', h.counts.kirim], ['bon', 'fld.hf_bon', h.counts.bon], ['bayar', 'fld.hf_bayar', h.counts.bayar]] : [];
  const canKoreksi = !!(can.correct || can.void);
  const price = c.masterPrice != null ? Number(c.masterPrice) : 0;
  const ph = (d && d.priceHistory) || [];
  let left = shown;
  const info = [
    ['fld.i_address', c.address || '—'],
    ['fld.i_phone', c.phone || '—'],
    ['fld.i_days', (c.deliveryDays || []).length ? (c.deliveryDays || []).join(', ') + (c.fixedDays ? ' · ' + trFl('fld.fixedLow') : '') : '—'],
    ['fld.i_armada', c.armada || '—'],
    ['fld.i_type', c.type ? String(c.type).charAt(0).toUpperCase() + String(c.type).slice(1) : '—'],
    ['fld.i_pin', c.gaps.titik ? trFl('fld.i_none') : (c.locationAccuracy != null ? trFl('fld.i_pinAcc', { m: Math.round(c.locationAccuracy) }) : trFl('fld.i_saved')) + (c.locationSetByName ? ' · ' + c.locationSetByName : '')],
    ['fld.i_photo', c.gaps.foto ? trFl('fld.i_none') : trFl('fld.i_saved') + (c.locationPhotoAt ? ' · ' + fldWhen(c.locationPhotoAt) : '')],
    ['fld.i_since', c.createdAt ? fldWhen(c.createdAt) : '—'],
  ];
  return (
    <div className={'mlap-screen mlap-cdetail' + (embedded ? ' embedded' : '')}>
      {embedded ? null : <FldTop title={trFl('fld.custDetailT')} onBack={onBack} kind="back" />}
      <div className="mlap-body">
        <div className="mlap-chero">
          <span className="mlap-ava xl" aria-hidden="true" style={{ background: t[0], color: t[1] }}>{fldInitials(c.name)}</span>
          <span className="mlap-grow">
            <span className="mlap-custhead-nm">{c.name}</span>
            <span className="mlap-custhead-sb">{[c.code, c.address].filter(Boolean).join(' · ')}</span>
            {c.active === false ? <span className="mlap-htag neg">{trFl('fld.i_inactive')}</span> : null}
          </span>
        </div>
        <CustHousePhoto photoId={c.locationPhotoId} />
        <CustIdCard phone={c.phone} />
        <div className="mlap-contacts">
          <FldLinkBtn href={links.nav} className="mlap-ctile" newTab><FldSvg n="navigate" s={18} />{trFl('fld.navigate')}</FldLinkBtn>
          <FldLinkBtn href={links.tel} className="mlap-ctile"><FldSvg n="phone" s={18} />{trFl('fld.call')}</FldLinkBtn>
          {links.wa ? <FldLinkBtn href={links.wa} className="mlap-ctile" newTab><FldSvg n="wa" s={18} />{trFl('fld.wa')}</FldLinkBtn>
            : fix ? <button type="button" className="mlap-ctile miss" onClick={fix}><FldSvg n="wa" s={18} />{trFl('fld.fillWaTile')}</button>
              : <FldLinkBtn href="" className="mlap-ctile"><FldSvg n="wa" s={18} />{trFl('fld.wa')}</FldLinkBtn>}
        </div>
        <div className="mlap-card mlap-kpi4">
          <div><span className="sb">{trFl('fld.bonNow')}</span>{c.sisaBon == null ? <b>—</b> : c.sisaBon > 0 ? <b className="bon">{FIELDLOGIC.fmtRp(c.sisaBon)}</b> : <b className="ok">{trFl('fld.lunas')}</b>}</div>
          <div><span className="sb">{trFl('fld.heldAt')}</span><b>{c.gallonsHeld == null ? '—' : trFl('fld.nGalon', { n: c.gallonsHeld })}</b></div>
          <div><span className="sb">{trFl('fld.i_price')}</span><b>{price > 0 ? FIELDLOGIC.fmtRp(price) : '—'}</b></div>
          <div><span className="sb">{trFl('fld.i_last')}</span><b>{last}</b></div>
        </div>
        {warns.length ? <span className="mlap-gtags">{warns.map((k) => <span key={k} className="mlap-gtag">{trFl(k)}</span>)}</span> : null}
        <FldSeg size="sm" label={trFl('fld.custDetailT')} value={seg} onChange={setSeg} options={[['riwayat', trFl('fld.h_tab')], ['info', trFl('fld.i_tab')]]} />
        {seg === 'riwayat' ? (
          err ? <FldNotice tone="warn" alert title={trFl('fld.loadErr')} sub={fldErrMsg(err)} action={trFl('fld.retry')} onAction={() => setReload((n) => n + 1)} />
            : !h ? <div className="mlap-empty">{trFl('fld.loading')}</div>
              : (
                <>
                  {st.deliveries ? (
                    <div className="mlap-hstats" role="note">
                      <span><b>{st.deliveries}</b>{trFl('fld.hs_kirim')}</span>
                      <span><b>{String(st.avgQty).replace('.', ',')}</b>{trFl('fld.hs_avg')}</span>
                      <span><b>{st.everyDays != null ? trFl('fld.hs_everyN', { n: st.everyDays }) : '—'}</b>{trFl('fld.hs_every')}</span>
                      <span><b>{st.qty30}</b>{trFl('fld.hs_30')}</span>
                    </div>
                  ) : null}
                  <div className="mlap-hscroll" role="group" aria-label={trFl('fld.filter')}>
                    {chips.map(([k, key, n]) => <button key={k} type="button" className={'mlap-fchip' + (filter === k ? ' on' : '')} aria-pressed={filter === k} onClick={() => { setFilter(k); setShown(FLD_HIST_PAGE); }}>{trFl(key, { n })}</button>)}
                  </div>
                  {api.mode === 'latihan' ? <div className="mlap-note">{trFl('fld.h_latihan')}</div> : null}
                  {h.limitedFrom ? <div className="mlap-note">{trFl('fld.h_limited', { d: fldShortDay(h.limitedFrom) })}</div> : null}
                  {h.groups.length ? h.groups.map((g) => {
                    if (left <= 0) return null;
                    const rows = g.rows.slice(0, left); left -= rows.length;
                    return (
                      <section key={g.key} className="mlap-hgroup" aria-label={fldMonthLabel(g.key)}>
                        <div className="mlap-hgroup-hd"><b>{fldMonthLabel(g.key)}</b><span>{[g.qty ? trFl('fld.nGalon', { n: g.qty }) : '', g.amount ? FIELDLOGIC.fmtRp(g.amount) : ''].filter(Boolean).join(' · ')}</span></div>
                        <div className="mlap-card">
                          {rows.map((r) => <FldHistRow key={r.src + r.id} api={api} r={r} open={openId === r.id} canKoreksi={canKoreksi}
                            onToggle={() => setOpenId(openId === r.id ? '' : r.id)} onKoreksi={() => onAction('koreksi', { transactionId: r.id, customerId: c.id })} />)}
                        </div>
                      </section>
                    );
                  }) : <div className="mlap-empty">{trFl(filter === 'all' ? 'fld.h_none' : 'fld.h_noneFilter')}</div>}
                  {h.rows.length > shown ? <button type="button" className="mlap-btn ghost mlap-wide" onClick={() => setShown(shown + FLD_HIST_PAGE)}>{trFl('fld.h_more', { n: h.rows.length - shown })}</button> : null}
                </>
              )
        ) : (
          <>
            <div className="mlap-card">
              <dl className="mlap-dl wide">
                {info.map(([k, v]) => <div key={k}><dt>{trFl(k)}</dt><dd>{v}</dd></div>)}
              </dl>
            </div>
            {ph.length ? (
              <section className="mlap-hgroup" aria-label={trFl('fld.i_priceHist')}>
                <div className="mlap-hgroup-hd"><b>{trFl('fld.i_priceHist')}</b></div>
                <div className="mlap-card">
                  {ph.slice(0, 5).map((x, i) => (
                    <div key={x.id || i} className="mlap-row">
                      <span className="mlap-grow"><span className="nm">{FIELDLOGIC.fmtRp(x.oldPrice)} → {FIELDLOGIC.fmtRp(x.newPrice)}</span><span className="sb">{[x.changedAt ? fldWhen(x.changedAt) : '', x.changedByName].filter(Boolean).join(' · ')}</span></span>
                    </div>
                  ))}
                </div>
              </section>
            ) : null}
          </>
        )}
        {acts.length ? (
          <section className="mlap-hgroup" aria-label={trFl('fld.i_actions')}>
            <div className="mlap-hgroup-hd"><b>{trFl('fld.i_actions')}</b></div>
            <div className="mlap-card">
              {acts.map(([k, ico, key]) => (
                <button key={k} type="button" className="mlap-actrow" onClick={() => onAction(k, c)}>
                  <FldSvg n={ico} s={17} /><span className="mlap-grow">{trFl(key)}</span><FldSvg n="chevron" s={13} sw={2.4} />
                </button>
              ))}
            </div>
          </section>
        ) : null}
        <div className="mlap-ctaspace" />
      </div>
    </div>
  );
}

const fldPt = (p) => p.lat.toFixed(5).replace('.', ',') + ', ' + p.lng.toFixed(5).replace('.', ',');

// A small still map of the customer's point (mockup Lengkapi): no gestures, the OSM licence line kept;
// a plain ground with a grey pin when there is no point yet or the map cannot load.
function FldMiniMap({ pt, caption }) {
  const el = uRfl(null);
  uEfl(() => {
    if (!pt || !el.current) return undefined;
    let live = true; let map = null; let tm = 0;
    // built after the form has painted: building it in the same task froze the form's opening (~130 ms on a phone)
    const go = () => znLoadLeaflet().then((L) => {
      if (!live || !el.current) return;
      map = L.map(el.current, { zoomControl: false, attributionControl: false, dragging: false, scrollWheelZoom: false, doubleClickZoom: false, boxZoom: false, keyboard: false, touchZoom: false });
      L.control.attribution({ position: 'topright', prefix: false }).addTo(map);
      L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19, attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>',
      }).addTo(map);
      map.setView([pt.lat, pt.lng], 17);
    }).catch(() => { /* the plain ground stays */ });
    const raf = requestAnimationFrame(() => { tm = setTimeout(go, 0); });
    return () => { live = false; cancelAnimationFrame(raf); clearTimeout(tm); if (map) map.remove(); };
  }, [pt ? pt.lat + ',' + pt.lng : '']);
  return (
    <div className="mlap-minimap">
      <div ref={el} className="mlap-map mlap-minimap-map" aria-hidden="true" />
      <svg className={'mlap-minipin' + (pt ? ' on' : '')} width="28" height="36" viewBox="0 0 24 32" aria-hidden="true"><path d="M12 31s-10-10-10-18a10 10 0 0 1 20 0c0 8-10 18-10 18z" fill="currentColor" stroke="#FFFFFF" strokeWidth="2" /><circle cx="12" cy="12" r="4" fill="#FFFFFF" /></svg>
      <span className="mlap-glass mlap-minicap">{caption}</span>
    </div>
  );
}

// LENGKAPI DATA — the three things a route needs from a customer: a location pin (from this phone's
// GPS, or dragged on the map), a WhatsApp number, a location photo. "Simpan" saves only what changed.
function FldComplete({ api, cust: c, onPin, onDone, onBack }) {
  const g = FIELDLOGIC.gapsOf(c);
  const [gps, setGps] = uSfl(null);
  const [locBusy, setLocBusy] = uSfl(false);
  const [wa, setWa] = uSfl(c.phone || '');
  const [photo, setPhoto] = uSfl(null);
  const [busy, setBusy] = uSfl(false);
  const [err, setErr] = uSfl('');
  const takeGps = () => { setLocBusy(true); setErr(''); fldGeo(12000).then((p) => { setLocBusy(false); if (p) setGps(p); else setErr(trFl('fld.noGps')); }); };
  const changed = !!gps || (wa.trim() !== String(c.phone || '').trim() && wa.trim() !== '') || !!photo;
  const save = async () => {
    setBusy(true); setErr('');
    try {
      if (gps) await api.setLocation(c.id, { lat: gps.lat, lng: gps.lng, accuracy: gps.accuracy, method: 'gps' });
      if (wa.trim() && wa.trim() !== String(c.phone || '').trim()) await api.setPhone(c.id, wa);
      if (photo) await api.setLocationPhoto(c.id, photo.id);
      onDone(trFl('fld.dataSaved', { name: c.name }));
    } catch (e) { setErr(fldErrMsg(e)); }
    setBusy(false);
  };
  const had = typeof c.lat === 'number' && typeof c.lng === 'number';
  const pt = gps ? { lat: gps.lat, lng: gps.lng } : had ? { lat: c.lat, lng: c.lng } : null;
  const okTitik = !g.titik || !!gps; const okWa = !g.wa || !!wa.trim(); const okFoto = !g.foto || !!photo;
  const done = (okTitik ? 1 : 0) + (okWa ? 1 : 0) + (okFoto ? 1 : 0);
  const cap = gps ? trFl('fld.ptGps', { p: fldPt(gps), m: Math.round(gps.accuracy || 0) }) : had ? trFl('fld.pinHave') : trFl('fld.ptNone');
  const dot = (ok) => <span className={'mlap-dot ' + (ok ? 'ok' : 'miss')} aria-hidden="true">{ok ? '✓' : '!'}</span>;
  return (
    <div className="mlap-screen">
      <FldTop title={trFl('fld.completeT2')} onBack={onBack} backLabel={trFl('fld.later')} />
      <div className="mlap-body">
        <FldCustHead name={c.name} sub={[c.code, c.address].filter(Boolean).join(' · ')} aside={<span className={'mlap-prog' + (done === 3 ? ' ok' : '')}>{trFl('fld.dataN', { n: done })}</span>} />
        <div className="mlap-card">
          <FldMiniMap pt={pt} caption={cap} />
          <div className="mlap-sec">
            <div className="mlap-check">{dot(okTitik)}<b className="mlap-grow">{trFl('fld.chkTitik')}</b><span className="mlap-secnote">{trFl('fld.pinNeeded')}</span></div>
            <div className="mlap-twobtn">
              <button type="button" className={'mlap-btn ' + (gps || had ? 'line' : 'soft2')} disabled={locBusy} onClick={takeGps}><FldSvg n="crosshair" s={16} sw={2.2} />{locBusy ? trFl('fld.locating') : gps ? trFl('fld.gpsAgain') : trFl('fld.useMyLoc2')}</button>
              <button type="button" className="mlap-btn line blue" onClick={() => onPin(c)}><FldSvg n="pinMove" s={16} />{trFl('fld.dragOnMap')}</button>
            </div>
            <span className="mlap-hint">{trFl('fld.gpsDrift')}</span>
          </div>
        </div>
        <div className="mlap-card mlap-sec">
          <div className="mlap-check">{dot(okWa)}<b className="mlap-grow">{trFl('fld.chkWa')}</b><span className="mlap-secnote">{trFl('fld.waFor')}</span></div>
          <input className="mlap-text" type="tel" inputMode="tel" placeholder="0812 3456 7890" aria-label={trFl('fld.chkWa')} value={wa} onChange={(e) => setWa(e.target.value.slice(0, 20))} />
        </div>
        <FldPhoto api={api} value={photo} onChange={setPhoto} hintKey="fld.locPhotoHint" title={trFl('fld.chkFoto')} optional={!g.foto} w={100} h={76} />
        {err && <div className="mlap-err" role="alert">{err}</div>}
      </div>
      <FldCtaBar><button type="button" className="mlap-btn primary" disabled={busy || !changed} onClick={save}>{trFl('fld.saveCust')}</button></FldCtaBar>
    </div>
  );
}

// ATUR TITIK LOKASI — a draggable pin over the map. The phone's own GPS fix (with its accuracy circle)
// and the old pin are shown; a pin moved > 150 m from the phone asks to confirm. Saved as a "geser" with
// the device fix, so the history says how far it was moved from the phone's GPS.
// `office` (the office phone view, dist-hp-cust.jsx): ✕ + a paste-link button, the distance from the OLD
// point, and office.beforeSave({ pin, dev }) instead of the far-from-phone confirm (null cancels, an object
// joins the body). Without it — Mode Lapangan — nothing here changes.
function FldPinMap({ api, cust: c, depot, rules, onDone, onBack, office }) {
  const had = typeof c.lat === 'number' && typeof c.lng === 'number';
  // SATELIT (owner 2026-10-02): Esri World Imagery with the owner's ArcGIS key (Aturan lapangan); no key → no switch
  const satKey = (rules && rules.satelliteKey) || '';
  const [layer, setLayer] = uSfl('peta');
  const [satErr, setSatErr] = uSfl(false);
  const layersRef = uRfl(null);
  const [dev, setDev] = uSfl(null);
  const [pin, setPin] = uSfl(had ? { lat: c.lat, lng: c.lng } : null);
  const [mapErr, setMapErr] = uSfl(false);
  const [askFar, setAskFar] = uSfl(false);
  const [busy, setBusy] = uSfl(false);
  const [err, setErr] = uSfl('');
  const [mapReady, setMapReady] = uSfl(false);   // the phone's fix may arrive before or after the map
  const [fallback, setFallback] = uSfl(false);   // no old pin + no GPS: started at the warehouse — must be moved
  const [moved, setMoved] = uSfl(false);
  const mapEl = uRfl(null);
  const mapRef = uRfl(null);
  const userRef = uRfl(false);   // a drag or zoom by the user (the first centring is not "moved")
  const [lift, setLift] = uSfl(false);
  if (office) {
    office.mapRef = mapRef;
    // a pasted link moves the map for the user: it counts as their move (a no-GPS start can then be saved)
    office.setView = (ll, z) => { userRef.current = true; if (mapRef.current) mapRef.current.setView(ll, z); };
  }
  uEfl(() => {
    let live = true;
    fldGeo(12000).then((p) => {
      if (!live) return; setDev(p);
      if (!had) { const st = FIELDLOGIC.pinStart({ cust: c, device: p, depot }); setPin((cur) => cur || st.pin); setFallback(office ? true : st.fallback); }   // office: a phone in the office is not the customer — always move
    });
    return () => { live = false; };
  }, []);
  uEfl(() => {
    if (!pin || !mapEl.current || mapRef.current) return undefined;
    let live = true;
    znLoadLeaflet().then((L) => {
      if (!live || !mapEl.current) return;
      const map = L.map(mapEl.current, { zoomControl: false, attributionControl: false });
      L.control.attribution({ position: 'topright', prefix: false }).addTo(map);   // the sheet covers the bottom: keep the OSM / Esri credit visible
      mapRef.current = map;
      const osm = L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19, attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>',
      });
      const sat = satKey ? L.tileLayer('https://ibasemaps-api.arcgis.com/arcgis/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}?token=' + encodeURIComponent(satKey), {
        maxZoom: 19, attribution: 'Powered by <a href="https://www.esri.com" target="_blank" rel="noopener">Esri</a> | Esri, Maxar, Earthstar Geographics',
      }) : null;
      // a bad / expired key (or no network to Esri) → back to the street map, and say why. Once a satellite
      // tile has loaded, a later failed tile is a patchy signal, not a bad key: the driver stays on Satelit.
      let satOk = false;
      if (sat) {
        sat.on('tileload', () => { satOk = true; });
        sat.on('tileerror', () => { if (!satOk) { setSatErr(true); setLayer('peta'); } });
      }
      osm.addTo(map);
      layersRef.current = { osm, sat };
      if (had) L.circleMarker([c.lat, c.lng], { radius: 7, color: '#5B6B75', weight: 2, fillOpacity: 0.15, interactive: false }).addTo(map);
      map.setView([pin.lat, pin.lng], 18);   // first: Leaflet fires zoomstart on the first view, which is not the driver
      // the pin stays in the centre; the driver moves the map under it (drag anywhere, mockup)
      map.on('move', () => { const ce = map.getCenter(); setPin({ lat: ce.lat, lng: ce.lng }); });
      map.on('dragstart zoomstart', () => { userRef.current = true; setLift(true); });
      map.on('keydown', () => { userRef.current = true; });   // M7: arrow-key panning is the driver too
      map.on('moveend', () => { setLift(false); if (userRef.current) setMoved(true); });
      setMapReady(true);
    }).catch(() => { if (live) setMapErr(true); });
    return () => { live = false; };
  }, [!!pin]);
  // the phone's fix + its accuracy circle, whichever of (fix, map) comes last
  uEfl(() => {
    if (!dev || !mapReady || !mapRef.current || !window.L) return undefined;
    const L = window.L;
    const circle = L.circle([dev.lat, dev.lng], { radius: Math.max(5, dev.accuracy || 0), color: '#065489', weight: 1, fillOpacity: 0.08, interactive: false }).addTo(mapRef.current);
    const dot = L.circleMarker([dev.lat, dev.lng], { radius: 5, color: '#fff', weight: 2, fillColor: '#065489', fillOpacity: 1, interactive: false }).addTo(mapRef.current);
    return () => { circle.remove(); dot.remove(); };
  }, [dev, mapReady]);
  // swap the base layer (the pin, the GPS circle and the view stay where they are)
  uEfl(() => {
    const map = mapRef.current; const ls = layersRef.current;
    if (!mapReady || !map || !ls) return;
    const want = layer === 'sat' && ls.sat ? ls.sat : ls.osm; const other = want === ls.osm ? ls.sat : ls.osm;
    if (other && map.hasLayer(other)) map.removeLayer(other);
    if (!map.hasLayer(want)) want.addTo(map);
  }, [layer, mapReady]);
  uEfl(() => () => { if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; } }, []);
  const mv = FIELDLOGIC.pinMove({ device: dev, pin });
  const fromOld = office && had && pin ? Math.round(FIELDLOGIC.distM({ lat: c.lat, lng: c.lng }, pin)) : null;
  const save = (confirmFar) => {
    if (!pin) return;
    if (!office && mv.far && !confirmFar) { setAskFar(true); return; }
    const go = (extra) => {
      setBusy(true); setErr('');
      api.setLocation(c.id, Object.assign({ lat: pin.lat, lng: pin.lng, accuracy: dev ? dev.accuracy : null, method: 'geser', deviceLat: dev ? dev.lat : undefined, deviceLng: dev ? dev.lng : undefined, deviceAccuracy: dev ? dev.accuracy : undefined }, extra))
        .then(() => onDone(trFl('fld.pinSaved', { name: c.name }), { lat: pin.lat, lng: pin.lng }))
        .catch((e) => setErr(fldErrMsg(e)))
        .finally(() => setBusy(false));
    };
    if (!office) { go({}); return; }
    Promise.resolve(office.beforeSave({ pin, dev })).then((x) => { if (x) go(x); });
  };
  return (
    <div className="mlap-screen">
      {!mapErr && pin ? (
        <div className="mlap-pinwrap">
          <div ref={mapEl} className="mlap-map mlap-pinmap" role="application" aria-label={trFl('fld.pinT')} />
          <span className={'mlap-centerpin' + (lift ? ' up' : '')} aria-hidden="true"><span className="mlap-centerpin-dot" /></span>
          <span className={'mlap-centerpin-shadow' + (lift ? ' up' : '')} aria-hidden="true" />
        </div>
      ) : null}
      <div className="mlap-mapbar">
        {office ? <FldCloseX onClick={onBack} label={trFl('fld.close')} /> : <button type="button" className="mlap-round" aria-label={trFl('fld.back')} onClick={onBack}><FldSvg n="back" s={18} sw={2.4} /></button>}
        {satKey ? <div className="mlap-glass mlap-mapseg" role="group" aria-label={trFl('fld.mapType')}>{[['peta', trFl('fld.layerMap')], ['sat', trFl('fld.layerSat')]].map(([k, t]) => <button key={k} type="button" aria-pressed={layer === k} className={'mlap-mapseg-b' + (layer === k ? ' on' : '')} onClick={() => { setSatErr(false); setLayer(k); }}>{t}</button>)}</div> : <div className="mlap-glass mlap-mappill">{trFl('fld.pinT')}</div>}
        {office ? <button type="button" className="mlap-round" aria-label={trFl('dhp.pinPaste')} onClick={() => office.onPaste()}><FldSvg n="note" s={19} /></button> : null}
        {dev && mapReady ? <button type="button" className="mlap-round mlap-map-locate" aria-label={trFl('fld.useMyLoc')} onClick={() => mapRef.current.setView([dev.lat, dev.lng], 18)}><FldSvg n="locate" s={19} /></button> : <span className="mlap-roundsp" aria-hidden="true" />}
      </div>
      {!mapErr && pin ? <div className="mlap-glass mlap-pinhint"><FldSvg n="hand" s={14} sw={2.2} />{trFl('fld.pinPan')}</div> : null}
      {mapErr || !pin ? <div className="mlap-mapempty">{mapErr ? <FldNotice tone="info" title={trFl('fld.mapOff')} sub={trFl('fld.pinNoMap')} /> : <div className="mlap-empty">{trFl('fld.locating')}</div>}</div> : null}
      <div className="mlap-pinsheet">
        <div className="mlap-grab" aria-hidden="true" />
        <div className="mlap-pinsheet-hd"><b>{trFl('fld.pinOf', { name: c.name })}</b>{c.code ? <span className="sb">{c.code}</span> : null}</div>
        <div className="mlap-card">
          <div className="mlap-kv"><span>{trFl('fld.coords')}</span><b>{pin ? fldPt(pin) : '—'}</b></div>
          {office
            ? <div className="mlap-kv"><span>{trFl('fld.fromOld')}</span><b className={fromOld != null && fromOld > 150 ? 'far' : fromOld != null && fromOld >= 3 ? 'moved' : ''}>{fromOld == null ? '—' : fromOld < 3 ? trFl('fld.pinSame') : trFl('fld.pinMoved', { m: fromOld })}</b></div>
            : <div className="mlap-kv"><span>{dev ? trFl('fld.fromDevice', { m: Math.round(dev.accuracy || 0) }) : trFl('fld.noGps')}</span><b className={mv.far ? 'far' : mv.meters != null && mv.meters >= 3 ? 'moved' : ''}>{mv.meters == null ? '—' : mv.meters < 3 ? trFl('fld.pinSame') : trFl('fld.pinMoved', { m: mv.meters })}</b></div>}
        </div>
        <span className={'mlap-after' + ((!office && mv.far) || (fallback && !moved) ? ' far' : '')}>{!office && mv.far ? trFl('fld.pinFar') : fallback && !moved ? trFl(office ? 'dhp.pinMustMove' : 'fld.pinNoGpsMove') : trFl('fld.pinAudit')}</span>
        {mapErr && dev ? <button type="button" className="mlap-btn mlap-wide" onClick={() => setPin({ lat: dev.lat, lng: dev.lng })}>{trFl('fld.useMyLoc')}</button> : null}
        {satErr ? <div className="mlap-err" role="alert">{trFl('fld.satErr')}</div> : null}
        {err && <div className="mlap-err" role="alert">{err}</div>}
        {office && office.extra ? office.extra : null}
        <button type="button" className="mlap-btn primary mlap-wide" disabled={busy || !pin || (fallback && !moved)} onClick={() => save(false)}>{trFl('fld.savePin')}</button>
      </div>
      {askFar && <FldSheet title={trFl('fld.pinFarT', { m: mv.meters })} body={trFl('fld.pinFarB')} confirmLabel={trFl('fld.savePin')} onClose={() => setAskFar(false)} onConfirm={() => { setAskFar(false); save(true); }} />}
    </div>
  );
}

// TAMBAH STOP (mockup Tambah stop board) — a sheet: search; today's stops that cannot join the route (no
// pin — set it); other customers as radio rows tagged with whether they have a pin; what the pick means
// (no pin → the end of the list, set the pin now; a pin → it joins the route); how many gallons; add.
function FldAddStop({ api, preset, can, onPin, onDone, onBack }) {
  const drag = useFldSheetDrag(onBack);
  const [d, setD] = uSfl(null);
  const [err, setErr] = uSfl(null);
  const [q, setQ] = uSfl('');
  const [pick, setPick] = uSfl(preset || null);
  const [qty, setQty] = uSfl(1);
  const [busy, setBusy] = uSfl(false);
  const [msg, setMsg] = uSfl('');
  uEfl(() => { let live = true; Promise.all([api.board(), api.customers()]).then(([board, customers]) => { if (live) setD({ board, customers }); }).catch((e) => { if (live) setErr(e); }); return () => { live = false; }; }, [api]);
  const sheet = (body, cta) => (
    <>
      <button type="button" className="mlap-scrim" aria-label={trFl('fld.cancel')} onClick={onBack} />
      <div className="mlap-sheet tall" role="dialog" aria-modal="true" aria-label={trFl('fld.addStopT')} ref={drag.ref} style={drag.style}>
        <FldGrab handle={drag.handle} />
        <FldSheetHead title={trFl('fld.addStopT')} onClose={onBack} />
        <div className="mlap-sheet-body">{body}</div>
        {cta ? <div className="mlap-sheet-cta">{cta}</div> : null}
      </div>
    </>
  );
  if (err) return sheet(<FldNotice tone="warn" alert title={trFl('fld.loadErr')} sub={fldErrMsg(err)} />);
  if (!d) return sheet(<div className="mlap-empty">{trFl('fld.loading')}</div>);
  const cand = FIELDLOGIC.addStopCandidates({ board: d.board, customers: d.customers, q });
  const pickHasPin = !!pick && typeof pick.lat === 'number' && typeof pick.lng === 'number';
  const onBoard = !!pick && d.board.some((s) => s.customerId === pick.id && s.status !== 'batal');   // already on today's route (also a "Tambah ke hari ini" from the customer sheet)
  const save = () => {
    setBusy(true); setMsg('');
    api.addStop({ customerId: pick.id, qty }).then(() => onDone(trFl('fld.stopAdded', { name: pick.name }))).catch((e) => setMsg(fldErrMsg(e))).finally(() => setBusy(false));
  };
  return sheet(
    <>
      <label className="mlap-searchbox"><FldSvg n="search" s={16} sw={2.2} /><input type="search" placeholder={trFl('fld.searchCust')} aria-label={trFl('fld.searchCust')} value={q} onChange={(e) => setQ(e.target.value)} /></label>
      {cand.noPin.length > 0 && !q.trim() && (
        <>
          <div className="mlap-label">{trFl('fld.noPinToday')}</div>
          <div className="mlap-card">
            {cand.noPin.map((s) => (
              <div key={s.id} className="mlap-row">
                <span className="mlap-grow"><span className="nm">{s.customerName}</span><span className="sb">{s.customerCode}</span></span>
                {can.location ? <button type="button" className="mlap-btn" onClick={() => onPin(fldCustFromStop(s))}>{trFl('fld.setPin')}</button> : null}
              </div>
            ))}
          </div>
        </>
      )}
      <div className="mlap-label">{trFl('fld.otherCust')}</div>
      <div className="mlap-card">
        {cand.others.length ? cand.others.slice(0, 60).map((c) => (
          <button key={c.id} type="button" className={'mlap-pickrow' + (pick && pick.id === c.id ? ' on' : '')} aria-pressed={!!(pick && pick.id === c.id)} onClick={() => setPick(c)}>
            <span className="mlap-radio" aria-hidden="true" />
            <span className="mlap-grow"><span className="nm">{c.name}</span><span className="sb">{[c.code, c.address].filter(Boolean).join(' · ')}</span></span>
            <span className={'mlap-tag ' + (c.gaps.titik ? 'warnt' : 'ok')}>{trFl(c.gaps.titik ? 'fld.tagNoPin' : 'fld.tagHasPin')}</span>
          </button>
        )) : <div className="mlap-empty">{trFl('fld.emptySeg')}</div>}
      </div>
      {pick && !pickHasPin ? (
        <div className="mlap-nopincard" role="alert">
          <div className="mlap-nopincard-hd"><FldSvg n="pinOff" s={18} sw={2.2} /><span className="mlap-grow"><b>{trFl('fld.pickNoPinT', { name: pick.name })}</b><span>{trFl('fld.pickNoPinB')}</span></span></div>
          {can.location ? <button type="button" className="mlap-btn mlap-pinnow" onClick={() => onPin(pick, true)}><FldSvg n="crosshair" s={16} sw={2.2} />{trFl('fld.setPinNow')}</button> : null}
        </div>
      ) : pick ? <FldNotice tone="info" title={trFl('fld.pickPinT', { name: pick.name })} /> : null}
      {onBoard ? <FldNotice tone="warn" title={trFl('fld.alreadyToday')} /> : null}
      {pick ? <div className="mlap-card"><FldStepper label={trFl('fld.qtyGalon')} value={qty} onChange={setQty} min={1} max={999} /></div> : null}
      {msg && <div className="mlap-err" role="alert">{msg}</div>}
    </>,
    <button type="button" className="mlap-btn primary" disabled={busy || qty < 1 || onBoard || !pick} onClick={save}>{trFl(pick ? (pickHasPin ? 'fld.addToRoute' : 'fld.addAtEnd') : 'fld.addStopCta')}</button>
  );
}

const FLD_BS = { lunas: 'fld.bs_lunas', sebagian: 'fld.bs_sebagian', belum: 'fld.bs_belum' };
// PEMBAYARAN BON — collect a customer's bon (cash or transfer) with a proof photo. The open bons are
// listed oldest first, as the payment settles them (view only — the server keeps the real balance). A bon
// tapped is what this payment is FOR (bonTargets, owner 2026-10-30): it settles first, the rest oldest first.
function FldPayBon({ api, cust: c, refs, onDone, onBack }) {
  const [detail, setDetail] = uSfl(null);
  const [pay, setPay] = uSfl(null);
  const [via, setVia] = uSfl('tunai');
  const [picked, setPicked] = uSfl([]);
  const togglePick = (id) => setPicked((p) => (p.indexOf(id) >= 0 ? p.filter((x) => x !== id) : p.concat(id)));
  const [photo, setPhoto] = uSfl(null);
  const [busy, setBusy] = uSfl(false);
  const [err, setErr] = uSfl('');
  const slot = 'bon:' + c.id;
  const [ref] = uSfl(() => refs.take(slot));   // kept until saved (a retry after leaving is not a second payment)
  uEfl(() => { let live = true; api.customerDetail(c.id).then((x) => { if (live) setDetail(x); }).catch(() => { if (live) setDetail({ transactions: [] }); }); return () => { live = false; }; }, [api, c.id]);
  const bon = detail && detail.sisaBon != null ? detail.sisaBon : (c.sisaBon || 0);
  const pv = FIELDLOGIC.payPreview({ sisaBon: bon, pay });
  const open = detail ? FIELDLOGIC.openBons(detail.transactions || []) : [];
  const save = () => {
    setBusy(true); setErr('');
    const body = { customerId: c.id, payAmount: pay, payMethod: via, clientRef: ref, proofPhotoId: photo.id, proofTakenAt: photo.takenAt, bonTargets: picked };
    if (typeof photo.lat === 'number' && typeof photo.lng === 'number') { body.proofLat = photo.lat; body.proofLng = photo.lng; }
    api.payBon(body).then((r) => { refs.done(slot); onDone(r && r.replay ? trFl('fld.replayed') : trFl('fld.paidDone', { name: c.name, v: FIELDLOGIC.fmtRp(pay) })); }).catch((e) => setErr(fldErrMsg(e))).finally(() => setBusy(false));
  };
  const settle = FIELDLOGIC.settleBons(open, pay, picked);
  const quick = [[bon, trFl('fld.payAllV', { v: FIELDLOGIC.fmtRp(bon) })], [open.length > 1 ? open[0].amount : 0, trFl('fld.payOldestV', { v: FIELDLOGIC.fmtRp(open.length > 1 ? open[0].amount : 0) })]].filter(([v]) => v > 0 && v <= bon);
  return (
    <div className="mlap-screen">
      <FldTop title={trFl('fld.catatBon')} onBack={onBack} />
      <div className="mlap-body">
        <FldCustHead name={c.name} sub={<>{c.code ? c.code + ' · ' : ''}{trFl('fld.sisaBonL')} <b className="mlap-bontxt">{FIELDLOGIC.fmtRp(bon)}</b></>} />
        {open.length > 0 && (
          <div className="mlap-card">
            {open.map((b, i) => (
              <button key={b.id} type="button" className={'mlap-bonrow mlap-bonpick' + (picked.indexOf(b.id) >= 0 ? ' on' : '')} aria-pressed={picked.indexOf(b.id) >= 0} onClick={() => togglePick(b.id)}>
                <span className="mlap-bontick" aria-hidden="true">{picked.indexOf(b.id) >= 0 ? <FldSvg n="check" s={13} sw={3} /> : null}</span>
                <span className="mlap-grow"><span className="nm">{b.txnDate}</span><span className="sb">{trFl('fld.nGalon', { n: b.qty })}{b.partial ? ' · ' + trFl('fld.partPaid') : ''}</span></span>
                <b>{FIELDLOGIC.fmtRp(b.amount)}</b>
                <span className={'mlap-bonst ' + settle[i]}>{trFl(FLD_BS[settle[i]])}</span>
              </button>
            ))}
            <div className="mlap-cardnote">{trFl(picked.length ? 'fld.btTap' : 'fld.oldestFirst')}</div>
          </div>
        )}
        <div className="mlap-card">
          <FldMoney label={trFl('fld.payAmount')} value={pay} onChange={setPay} />
          {quick.length ? <div className="mlap-chips mlap-pad">{quick.map(([v, l]) => <button key={l} type="button" className={'mlap-chip-b' + (pay === v ? ' on' : '')} aria-pressed={pay === v} onClick={() => setPay(v)}>{l}</button>)}</div> : null}
        </div>
        <FldSeg label={trFl('fld.payVia')} value={via} onChange={setVia} options={[['tunai', trFl('fld.m_tunai')], ['transfer', trFl('fld.m_transfer')]]} />
        <FldPhoto api={api} value={photo} onChange={setPhoto} hintKey={via === 'transfer' ? 'fld.payTfHint' : 'fld.payCashHint'} title={trFl(via === 'transfer' ? 'fld.payTfPhoto' : 'fld.payCashPhoto')} w={64} />
        <div className="mlap-card"><div className="mlap-kv tall"><span>{trFl('fld.bonAfterPay')}</span><b className={pv.rest === 0 ? 'big ok' : 'big bon'}>{pv.rest === 0 ? trFl('fld.lunas') : FIELDLOGIC.fmtRp(pv.rest)}</b></div></div>
        {pv.over > 0 ? <div className="mlap-warnline">{trFl('fld.payOver', { v: FIELDLOGIC.fmtRp(pv.over) })}</div> : null}
        {err && <div className="mlap-err" role="alert">{err}</div>}
      </div>
      <FldCtaBar hint={!photo ? trFl('fld.needPhoto') : ''}>
        <button type="button" className="mlap-btn primary" disabled={busy || !pv.ok || !photo} onClick={save}>{trFl('fld.payCta', { v: FIELDLOGIC.fmtRp(pay || 0) })}</button>
      </FldCtaBar>
    </div>
  );
}

const FLD_DMG_KINDS = [['pecah', 'fld.k_pecah'], ['bocor', 'fld.k_bocor'], ['retak', 'fld.k_retak'], ['hilang', 'fld.k_hilang']];
// GANTI RUGI GALON — a borrowed gallon broken or lost at the customer: recorded straight away (no
// approval — owner rule), priced by the owner's setting, paid cash / on bon / by transfer, photo required.
function FldDamage({ api, cust: c, rules, refs, onDone, onBack }) {
  const held = c.gallonsHeld == null ? 0 : c.gallonsHeld;
  const [qty, setQty] = uSfl(1);
  const [kind, setKind] = uSfl('');
  const [pay, setPay] = uSfl('tunai');
  const [photo, setPhoto] = uSfl(null);
  const [tfPhoto, setTfPhoto] = uSfl(null);   // optional transfer proof — without it the charge waits in Sisa Bon
  const [busy, setBusy] = uSfl(false);
  const [err, setErr] = uSfl('');
  const slot = 'dmg:' + c.id;
  const [ref] = uSfl(() => refs.take(slot));   // kept until saved (a retry after leaving is not a second charge)
  const pv = FIELDLOGIC.damagePreview({ qty, price: rules.hargaGantiRugiGalon, held, payMethod: pay, hasTransferProof: !!tfPhoto });
  const save = () => {
    setBusy(true); setErr('');
    api.gallonDamage(c.id, { qty, kind, payMethod: pay, photoId: photo.id, transferProofId: pay === 'transfer' && tfPhoto ? tfPhoto.id : undefined, clientRef: ref })
      .then((r) => { refs.done(slot); onDone(r && r.replay ? trFl('fld.replayed') : trFl('fld.dmgDone', { name: c.name, n: qty })); })
      .catch((e) => setErr(fldErrMsg(e)))
      .finally(() => setBusy(false));
  };
  return (
    <div className="mlap-screen">
      <FldTop title={trFl('fld.catatDmg')} onBack={onBack} />
      <div className="mlap-body">
        <FldCustHead name={c.name} sub={[c.code, trFl('fld.dmgSub')].filter(Boolean).join(' · ')} />
        {pv.blocked ? <FldNotice tone="warn" title={trFl(pv.blocked)} sub={trFl(pv.blocked + 'B')} /> : null}
        <div className="mlap-card">
          <FldStepper label={trFl('fld.dmgQty')} hint={trFl('fld.dmgFrom', { n: held })} value={qty} onChange={setQty} min={1} max={Math.max(1, held)} />
          <div className="mlap-cardsec"><span className="mlap-cardsec-t">{trFl('fld.dmgKindL')}</span><div className="mlap-chips">{FLD_DMG_KINDS.map(([k, key]) => <button key={k} type="button" className={'mlap-chip-b' + (kind === k ? ' on' : '')} aria-pressed={kind === k} onClick={() => setKind(k)}>{trFl(key)}</button>)}</div></div>
          <div className="mlap-kv tall"><span className="strong">{trFl('fld.dmgPrice')}</span>{pay === 'ganti_galon' ? <span className="mlap-pricebox">—</span> : <span className="mlap-pricebox">Rp <b>{Number(rules.hargaGantiRugiGalon || 0).toLocaleString('id-ID')}</b></span>}</div>
        </div>
        <div className="mlap-label">{trFl('fld.payVia')}</div>
        <FldSeg label={trFl('fld.payVia')} value={pay} onChange={setPay} options={[['tunai', trFl('fld.m_tunai')], ['bon', trFl('fld.toBon')], ['transfer', trFl('fld.m_transfer')], ['ganti_galon', trFl('fld.m_gantiGalon')]]} />
        <FldPhoto api={api} value={photo} onChange={setPhoto} hintKey="fld.dmgPhotoHint" title={trFl('fld.dmgPhotoT')} w={72} />
        {pay === 'transfer' ? <FldPhoto api={api} value={tfPhoto} onChange={setTfPhoto} hintKey="fld.tfPhotoHint" title={trFl('fld.tfPhotoT')} w={72} /> : null}
        <div className="mlap-card">
          <div className="mlap-kv"><span>{trFl('fld.galAtCust')}</span><b>{held + ' → ' + pv.heldAfter}</b></div>
          <div className="mlap-kv"><span>{trFl('fld.dmgToDepot')}</span><b>{kind === 'hilang' ? trFl('fld.dmgLost') : trFl('fld.nGalon', { n: qty })}</b></div>
          {pay === 'ganti_galon' ? <div className="mlap-kv"><span>{trFl('fld.newGalonIn')}</span><b>{trFl('fld.nGalon', { n: qty })}</b></div> : null}
          <div className="mlap-kv total"><span>{trFl(pv.totalKey)}</span><b>{FIELDLOGIC.fmtRp(pv.total)}</b></div>
        </div>
        {pay === 'ganti_galon' ? <div className="mlap-hint">{trFl('fld.poolKept')}</div> : null}
        <div className="mlap-hint">{trFl('fld.dmgNoApproval')}</div>
        {err && <div className="mlap-err" role="alert">{err}</div>}
      </div>
      <FldCtaBar><button type="button" className="mlap-btn primary" disabled={busy || !!pv.blocked || !kind || !photo} onClick={save}>{trFl('fld.dmgCta')}</button></FldCtaBar>
    </div>
  );
}

const FLD_EXP_CATS = [['bensin', 'fld.c_bensin', 'fuel'], ['parkir', 'fld.c_parkir', 'parking'], ['servis', 'fld.c_servis', 'wrench'], ['makan', 'fld.c_makan', 'food'], ['lainnya', 'fld.c_lainnya', 'dots']];
// PENGELUARAN — paid from the day's deposit, always in cash (owner rule: never "uang pribadi"), with a
// photo of the receipt. Fuel asks litres + odometer (kept in the note).
function FldExpense({ api, refs, onDone, onBack }) {
  const [cat, setCat] = uSfl('');
  const [amount, setAmount] = uSfl(null);
  const [liters, setLiters] = uSfl('');
  const [odo, setOdo] = uSfl('');
  const [note, setNote] = uSfl('');
  const [photo, setPhoto] = uSfl(null);
  const [today, setToday] = uSfl(null);
  const [busy, setBusy] = uSfl(false);
  const [err, setErr] = uSfl('');
  const slot = 'exp';
  const [ref] = uSfl(() => refs.take(slot));   // kept until saved (a retry after leaving is not a second expense)
  uEfl(() => { let live = true; api.daySummary().then((s) => { if (live) setToday(s && s.pengeluaran != null ? s.pengeluaran : null); }).catch(() => {}); return () => { live = false; }; }, [api]);
  const save = () => {
    setBusy(true); setErr('');
    api.addExpense(Object.assign(FIELDLOGIC.expenseBody({ category: cat, amount, liters, odometer: odo, note, photo }), { clientRef: ref }))
      .then((r) => { refs.done(slot); onDone(r && r.replay ? trFl('fld.replayed') : trFl('fld.expDone', { v: FIELDLOGIC.fmtRp(amount) })); })
      .catch((e) => setErr(fldErrMsg(e)))
      .finally(() => setBusy(false));
  };
  return (
    <div className="mlap-screen">
      <FldTop title={trFl('fld.catatExp')} onBack={onBack} />
      <div className="mlap-body">
        <div className="mlap-cats" role="group" aria-label={trFl('fld.catatExp')}>{FLD_EXP_CATS.map(([k, key, ico]) => <button key={k} type="button" className={'mlap-cat' + (cat === k ? ' on' : '')} aria-pressed={cat === k} onClick={() => setCat(k)}><FldSvg n={ico} s={20} /><span>{trFl(key)}</span></button>)}</div>
        <div className="mlap-card"><FldMoney label={trFl('fld.expAmount')} value={amount} onChange={setAmount} /></div>
        {cat === 'bensin' && (
          <div className="mlap-card mlap-two">
            <label><span>{trFl('fld.liters')}</span><input inputMode="decimal" placeholder={trFl('fld.litersPh')} value={liters} onChange={(e) => setLiters(e.target.value.replace(/[^0-9.,]/g, '').slice(0, 6))} /></label>
            <label><span>{trFl('fld.odometer')}</span><input inputMode="numeric" placeholder={trFl('fld.odoPh')} value={odo} onChange={(e) => setOdo(e.target.value.replace(/[^0-9]/g, '').slice(0, 7))} /></label>
          </div>
        )}
        <FldNotice tone="info" title={trFl('fld.expFromDeposit')} sub={trFl('fld.expFromDepositB')} />
        <input className="mlap-text" value={note} onChange={(e) => setNote(e.target.value.slice(0, 200))} placeholder={trFl('fld.noteOpt')} aria-label={trFl('fld.noteOpt')} />
        <FldPhoto api={api} value={photo} onChange={setPhoto} hintKey="fld.receiptHint" title={trFl('fld.receiptT')} w={64} h={76} />
        {today != null ? <div className="mlap-card"><div className="mlap-kv"><span>{trFl('fld.expToday', { v: FIELDLOGIC.fmtRp(today) })}</span></div></div> : null}
        {err && <div className="mlap-err" role="alert">{err}</div>}
      </div>
      <FldCtaBar><button type="button" className="mlap-btn primary" disabled={busy || !cat || !(amount > 0) || !photo} onClick={save}>{trFl('fld.expCta')}</button></FldCtaBar>
    </div>
  );
}
